import Amplify, { Auth } from 'aws-amplify';
import axios, { AxiosInstance } from 'axios';
import { cacheAdapterEnhancer } from 'axios-extensions';

export const amplifyConfig = () => {
  const config = {
    Auth: {
      // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
      identityPoolId: process.env.REACT_APP_AWS_IDENTITY_POOL_ID,
      // REQUIRED - Amazon Cognito Region
      region: process.env.REACT_APP_AWS_REGION,
      // OPTIONAL - Amazon Cognito User Pool ID
      userPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
      // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
      userPoolWebClientId: process.env.REACT_APP_AWS_USER_CLIENT_ID,
    },
    Storage: {
      AWSS3: {
        bucket: process.env.REACT_APP_AWS_BUCKET, //REQUIRED -  Amazon S3 bucket name
        region: process.env.REACT_APP_AWS_REGION, //OPTIONAL -  Amazon service region
      },
      customPrefix: {
        public: '',
      },
    },
  };
  Amplify.configure(config);
  Auth.configure(config);
};

export type CreateAxiosInstanceOptions = {
  // Cognito認証時、AuthorizationヘッダにBearerプレフィクスを付与する.
  bearerPrefix?: boolean;
};

// Cognito認証用のrequest interceptor。
// Auth.currentSession()は期限切れ時にrefresh tokenでidTokenを自動更新するため、
// 毎リクエスト直前に最新のJWTを取得することでセッションが1時間で切れる問題を回避する (Issue #842).
const attachCognitoAuthInterceptor = (instance: AxiosInstance, bearerPrefix: boolean) => {
  instance.interceptors.request.use(async (config) => {
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      config.headers = {
        ...config.headers,
        Authorization: bearerPrefix ? `Bearer ${idToken}` : idToken,
      };
    } catch {
      // Cognitoセッション無し (未ログイン / refresh token期限切れ) の場合は
      // Authorizationヘッダ未設定のままリクエストし、サーバ側の401応答に既存ハンドリングを任せる.
    }
    return config;
  });
};

export const createAxiosInstance = (
  API_BASE_URL: string,
  API_KEY?: string | null,
  // 後方互換のため引数は残置。Cognito認証時のJWTはrequest interceptorで毎回取得するため値は使われない.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _JWT_TOKEN?: string | null,
  options?: CreateAxiosInstanceOptions,
): AxiosInstance => {
  const bearerPrefix = options?.bearerPrefix === true;

  if (axios.defaults.adapter !== undefined) {
    // API_KEYが設定されている時はAPI_KEYで認証
    if (API_KEY) {
      return axios.create({
        baseURL: API_BASE_URL,
        headers: { 'x-api-key': API_KEY },
        adapter: cacheAdapterEnhancer(axios.defaults.adapter, { enabledByDefault: false, cacheFlag: 'useCache' }),
      });
    }
    // API_KEYが設定されていない時はCognito JWTで認証 (interceptorで毎回最新トークンを付与).
    const instance = axios.create({
      baseURL: API_BASE_URL,
      adapter: cacheAdapterEnhancer(axios.defaults.adapter, { enabledByDefault: false, cacheFlag: 'useCache' }),
    });
    attachCognitoAuthInterceptor(instance, bearerPrefix);
    return instance;
  }

  const instance = axios.create({ baseURL: API_BASE_URL });
  if (!API_KEY) {
    attachCognitoAuthInterceptor(instance, bearerPrefix);
  }
  return instance;
};
